import type { TemplateVariable } from "../api/queries/emailTemplateQueries";

export type PreviewMode = "single" | "multi";

export const DEFAULT_INVOICE_LIST_FORMAT =
  "{invoice_number} — {invoice_amount} ({due_date})";

export const LIST_ITEM_VARIABLE_KEYS = new Set([
  "invoice_number",
  "invoice_amount",
  "due_date",
  "invoice_date",
  "currency",
  "days_overdue",
  "days_until_due",
  "payment_link",
]);

const VARIABLE_ALIASES: Record<string, string> = {
  customer_name: "full_name",
  name: "full_name",
  customer_first_name: "first_name",
  customer_company: "company",
  customer_email: "email",
};

function canonicalVariableKey(key: string): string {
  return VARIABLE_ALIASES[key] ?? key;
}

const SAMPLE_SINGLE = {
  full_name: "Jane Cohen",
  first_name: "Jane",
  company: "Acme Ltd",
  email: "jane@acme.com",
  invoice_number: "INV-1042",
  invoice_numbers: "INV-1042",
  invoice_amount: "$1,250.00",
  total_amount: "$1,250.00",
  currency: "USD",
  due_date: "March 15, 2026",
  invoice_date: "February 1, 2026",
  days_overdue: "12",
  days_until_due: "0",
  payment_link: '<a href="https://pay.example.com">pay here</a>',
  invoice_list: "<ul><li>INV-1042 — $1,250.00 (due Mar 15)</li></ul>",
  invoice_count: "1",
  sender_name: "David Mint",
  organization_name: "Datamint",
};

const SAMPLE_MULTI = {
  ...SAMPLE_SINGLE,
  invoice_numbers: "INV-1042, INV-1043",
  total_amount: "$3,400.00",
  invoice_list:
    "<ul><li>INV-1042 — $1,250.00 (due Mar 15)</li><li>INV-1043 — $2,150.00 (due Mar 22)</li></ul>",
  invoice_count: "2",
};

const SAMPLE_SINGLE_HEBREW = {
  full_name: "ג'יין כהן",
  first_name: "ג'יין",
  company: "אקמי בע״מ",
  email: "jane@acme.com",
  invoice_number: "INV-1042",
  invoice_numbers: "INV-1042",
  invoice_amount: '1.250,00 ש"ח',
  total_amount: '1.250,00 ש"ח',
  currency: "ILS",
  due_date: "15.03.2026",
  invoice_date: "01.02.2026",
  days_overdue: "12",
  days_until_due: "0",
  payment_link: '<a href="https://pay.example.com">לתשלום כאן</a>',
  invoice_list:
    '<ul dir="rtl"><li>INV-1042 — 1.250,00 ש"ח (מועד 15.03)</li></ul>',
  invoice_count: "1",
  sender_name: "דוד מינט",
  organization_name: "דאטאמינט",
};

const SAMPLE_MULTI_HEBREW = {
  ...SAMPLE_SINGLE_HEBREW,
  invoice_numbers: "INV-1042, INV-1043",
  total_amount: '3.400,00 ש"ח',
  invoice_list:
    '<ul dir="rtl"><li>INV-1042 — 1.250,00 ש"ח (מועד 15.03)</li><li>INV-1043 — 2.150,00 ש"ח (מועד 22.03)</li></ul>',
  invoice_count: "2",
};

const SINGLE_ONLY_KEYS = new Set([
  "invoice_number",
  "invoice_amount",
  "due_date",
  "invoice_date",
  "days_overdue",
  "days_until_due",
  "payment_link",
]);

const PLACEHOLDER_RE = /\{([a-z_]+)\}/g;

function containsHebrew(text: string): boolean {
  return /[\u0590-\u05FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
}

function getSampleValues(
  mode: PreviewMode,
  isHebrew: boolean,
): Record<string, string> {
  if (isHebrew) {
    return mode === "multi" ? SAMPLE_MULTI_HEBREW : SAMPLE_SINGLE_HEBREW;
  }
  return mode === "multi" ? SAMPLE_MULTI : SAMPLE_SINGLE;
}

export function renderTemplatePreview(
  text: string,
  mode: PreviewMode,
  variables?: TemplateVariable[],
  isHebrew = false,
  invoiceListFormat?: string,
): string {
  if (!text) return "";

  const useHebrew = isHebrew || containsHebrew(text);
  const samples = getSampleValues(mode, useHebrew);
  const variableByKey = new Map((variables ?? []).map((v) => [v.key, v]));

  return text.replace(PLACEHOLDER_RE, (_match, key: string) => {
    const canonical = canonicalVariableKey(key);
    if (canonical === "invoice_list") {
      return renderInvoiceListPreview(
        invoiceListFormat ?? DEFAULT_INVOICE_LIST_FORMAT,
        mode,
        variables,
        useHebrew,
      );
    }
    const meta = variableByKey.get(canonical);
    if (meta?.scope === "single" && mode === "multi") {
      return "";
    }
    if (meta?.scope === "list_item" && mode === "multi") {
      return "";
    }
    if (
      meta?.scope === "multi" &&
      mode === "single" &&
      canonical === "invoice_count"
    ) {
      return samples.invoice_count ?? "1";
    }
    if (mode === "multi" && SINGLE_ONLY_KEYS.has(canonical)) {
      return "";
    }
    return samples[canonical as keyof typeof samples] ?? `{${key}}`;
  });
}

export function buildPreviewSubject(
  subject: string,
  mode: PreviewMode,
  variables?: TemplateVariable[],
  isHebrew = false,
): string {
  const useHebrew = isHebrew || containsHebrew(subject);
  const rendered = renderTemplatePreview(
    subject,
    mode,
    variables,
    useHebrew,
  ).trim();
  if (!rendered) return useHebrew ? "אין נושא" : "No subject";
  return rendered;
}

export function renderInvoiceListPreview(
  listFormat: string,
  mode: PreviewMode,
  variables?: TemplateVariable[],
  isHebrew = false,
): string {
  const format = listFormat.trim() || DEFAULT_INVOICE_LIST_FORMAT;
  const count = mode === "multi" ? 2 : 1;
  const items = Array.from({ length: count }, () => {
    const line = renderTemplatePreview(format, "single", variables, isHebrew);
    return `<li>${line || format}</li>`;
  });
  return `<ul>${items.join("")}</ul>`;
}

export function hasSingularVarsInMultiPreview(
  text: string,
  mode: PreviewMode,
): boolean {
  if (mode !== "multi") return false;
  return [...text.matchAll(PLACEHOLDER_RE)].some((match) =>
    SINGLE_ONLY_KEYS.has(match[1]),
  );
}

export const STARTER_TEMPLATE_BODY_HEBREW = `שלום {first_name},<br/>
<br/>
מצורף פירוט החשבוניות הפתוחות בחשבונכם:<br/>
{invoice_list}<br/>
<br/>
אם התשלום כבר בוצע, נשמח לקבל אסמכתא כדי לעדכן את המערכת.<br/>
<br/>
בברכה,<br/>
{sender_name}`;

export const STARTER_TEMPLATE_SUBJECT_HEBREW =
  "תזכורת תשלום — {company}";

export const STARTER_TEMPLATE_BODY = `Hello {first_name},<br/>
<br/>
I hope this email finds you well. Please find your outstanding invoice details below:<br/>
{invoice_list}<br/>
<br/>
If you have already sent payment, please reply with confirmation so we can update our records.<br/>
<br/>
Best regards,<br/>
{sender_name}`;

export const STARTER_TEMPLATE_SUBJECT = "Payment reminder — {company}";

export const STARTER_SINGLE_TEMPLATE_BODY = `Hello {first_name},

This is a reminder regarding invoice {invoice_number} for {invoice_amount}, due {due_date}.

{payment_link}

Best regards,
{sender_name}`;

export const STARTER_SINGLE_TEMPLATE_SUBJECT =
  "Invoice {invoice_number} — payment reminder";
